import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { RouteConfig } from '@app/routing/RenderRoutes'

import { SubscriptionsTradingPlatformsPage } from '@pages/Subscriptions/SubscriptionsTradingPlatforms/SubscriptionsTradingPlatforms.page'
import { SubscriptionsTradingPlatformsHelpPage } from '@pages/Subscriptions/SubscriptionsTradingPlatforms/SubscriptionsTradingPlatformsHelp.page'

import { useAccountReadContext } from '@context/AccountContextContext'
import { DateFilterContextProvider } from '@context/DateFilterContext'
import { useProductReadContext } from '@context/ProductContext'

import { MarketDataHelpPage } from './MarketData/MarketDataHelpPage'
import { MarketDataPage } from './MarketData/MarketDataPage'
import { SubscriptionsHistoryPage } from './SubscriptionsHistory/SubscriptionsHistory.page'

export const useSubscriptionRoutes = (): RouteConfig[] => {
  const { t } = useTranslation()
  const visible = useSubscriptionRoutesConditions()

  return useMemo(
    (): RouteConfig[] => [
      {
        path: 'subscriptions',
        condition: visible.isSubscriptionsVisible,
        meta: { breadcrumbLabel: t('Tabs.Subscriptions') },
        children: [
          {
            path: 'market-data',
            element: <MarketDataPage />,
            meta: { breadcrumbLabel: t('Market Data.title') },
            children: [
              {
                path: 'why-market-data',
                element: <MarketDataHelpPage />,
              },
            ],
          },
          {
            path: 'trading-platforms',
            element: <SubscriptionsTradingPlatformsPage />,
            meta: {
              breadcrumbLabel: t('Subscriptions.Trading Platforms'),
            },
            children: [
              {
                path: 'why-trading-platforms',
                element: (
                  <SubscriptionsTradingPlatformsHelpPage
                    title={t('Subscriptions.Trading Platforms')}
                    description={t(
                      'Subscriptions.A Trading Platform is the software that enables investors and traders'
                    )}
                  />
                ),
              },
              {
                path: 'why-third-party-trading-platforms',
                element: (
                  <SubscriptionsTradingPlatformsHelpPage
                    title={t('Subscriptions.What is a Third Party Trading Platform')}
                    description={t(
                      'Subscriptions.Tickmill gives you the possibility to connect your trading account'
                    )}
                  />
                ),
              },
            ],
          },
          {
            path: 'history',
            element: (
              <DateFilterContextProvider>
                <SubscriptionsHistoryPage />
              </DateFilterContextProvider>
            ),
            meta: { breadcrumbLabel: t('History') },
          },
        ],
      },
    ],
    [t, visible.isSubscriptionsVisible]
  )
}

interface RoutesConditions {
  isSubscriptionsVisible: boolean
}

export const useSubscriptionRoutesConditions = (): RoutesConditions => {
  const { isDefaultCFDProductType } = useProductReadContext()
  const { account } = useAccountReadContext()

  const isSubscriptionsVisible = useMemo(() => {
    if (isDefaultCFDProductType()) {
      return false
    }

    return !!account?.visibilityConfiguration.hasEtdTraderRoom
  }, [account?.visibilityConfiguration.hasEtdTraderRoom, isDefaultCFDProductType])

  return { isSubscriptionsVisible }
}
